import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { ScenarioIncident } from '../../types/ScenarioManagement';
import { StandardButton } from '../ui';

interface IncidentGridCardProps {
  incident: ScenarioIncident;
  index: number;
  onEdit: (incident: ScenarioIncident) => void;
  onDelete: (incidentId: string) => void;
  onToggle: (incidentId: string, enabled: boolean) => void;
  onReorder: (incidentId: string, newOrder: number) => void;
  readOnly?: boolean;
  totalIncidents: number;
  getSeverityColor: (severity: string) => string;
  formatTimeLimit: (seconds: number) => string;
}

export const IncidentGridCard: React.FC<IncidentGridCardProps> = React.memo(({
  incident,
  index,
  onEdit,
  onDelete,
  onToggle,
  onReorder,
  readOnly = false,
  totalIncidents,
  getSeverityColor,
  formatTimeLimit
}) => {
  const { isDark } = useTheme();

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className={`rounded-lg border ${
        isDark 
          ? 'bg-white/[0.03] border-white/10' 
          : 'bg-white border-gray-200'
      } ${!incident.enabled && 'opacity-60'}`}
    >
      {/* Incident Content */}
      <div className="p-4 flex flex-col h-full">
        {/* Header with Order and Actions */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {readOnly ? (
              <div className={`w-12 h-12 flex items-center justify-center text-center rounded-lg text-base font-semibold ${
                isDark 
                  ? 'bg-white/[0.08] border-white/20 text-white' 
                  : 'bg-gray-100 border-gray-300 text-gray-900'
              } border-2`}>
                {incident.order ?? index}
              </div>
            ) : (
              <input
                type="number"
                value={incident.order ?? index}
                onChange={(e) => {
                  const newOrder = parseInt(e.target.value) || 0;
                  onReorder(incident.id, Math.max(0, Math.min(newOrder, totalIncidents - 1)));
                }}
                className={`w-12 h-12 flex items-center justify-center text-center rounded-lg text-base font-semibold ${
                  isDark 
                    ? 'bg-white/[0.08] border-white/20 text-white hover:bg-white/[0.12] focus:bg-white/[0.15] focus:border-professional-blue-400' 
                    : 'bg-gray-100 border-gray-300 text-gray-900 hover:bg-gray-200 focus:bg-white focus:border-blue-500'
                } border-2 transition-all`}
                min="0"
                max={totalIncidents - 1}
              />
            )}
          </div>

          {/* Actions */}
          {!readOnly && (
            <div className="flex gap-1">
              <StandardButton
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit(incident);
                }}
                variant="ghost"
                size="small"
                title="Edit incident"
                className={`!p-1.5 ${
                  isDark 
                    ? '!text-professional-blue-400 hover:!text-professional-blue-300' 
                    : '!text-blue-600 hover:!text-blue-700'
                }`}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                </svg>
              </StandardButton>
              <StandardButton
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(incident.id);
                }}
                variant="ghost"
                size="small"
                title="Delete incident"
                className={`!p-1.5 ${
                  isDark 
                    ? '!text-red-400 hover:!text-red-300' 
                    : '!text-red-600 hover:!text-red-700'
                }`}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                </svg>
              </StandardButton>
            </div>
          )}
        </div>

        {/* Incident Info - Grows to fill space */}
        <div className="flex-1 space-y-2">
          <h4 className={`font-medium ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {incident.title}
          </h4>
          <p className={`text-sm ${
            isDark ? 'text-slate-300' : 'text-gray-600'
          }`}>
            {incident.description}
          </p>

          {/* Metadata */}
          <div className="flex items-center gap-4 mt-3">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              getSeverityColor(incident.severity)
            }`}>
              {incident.severity.toUpperCase()}
            </span>
            <span className={`text-sm ${
              isDark ? 'text-slate-400' : 'text-gray-500'
            }`}>
              ⏱️ {formatTimeLimit(incident.timeLimit)}
            </span>
            <span className={`text-sm ${
              isDark ? 'text-slate-400' : 'text-gray-500'
            }`}>
              📁 {incident.category}
            </span>
            <span className={`text-sm ${
              isDark ? 'text-slate-400' : 'text-gray-500'
            }`}>
              🎯 {incident.responseOptions?.length || 0} Response Options
            </span>
          </div>
        </div>

        {/* Bottom Section - Fixed at bottom */}
        <div className={`flex items-center justify-between mt-4 pt-4 border-t ${
          isDark ? 'border-white/10' : 'border-gray-200'
        }`}>
          {/* Checkbox - Bottom Left */}
          <input
            type="checkbox"
            checked={incident.enabled !== false}
            onChange={(e) => !readOnly && onToggle(incident.id, e.target.checked)}
            disabled={readOnly}
            className={`rounded focus:ring-2 ${
              isDark 
                ? 'border-white/20 text-professional-blue-400 focus:ring-professional-blue-400' 
                : 'border-gray-300 text-blue-600 focus:ring-blue-500'
            } ${readOnly ? 'cursor-not-allowed opacity-50' : ''}`}
            style={{ transform: 'scale(0.5)', transformOrigin: 'center' }}
          />
          
          {/* Active Status - Bottom Right */}
          <span className={`text-xs font-medium ${
            incident.enabled !== false
              ? isDark ? 'text-green-400' : 'text-green-600' 
              : isDark ? 'text-red-400' : 'text-red-600'
          }`}>
            {incident.enabled !== false ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for better performance
  return (
    prevProps.incident.id === nextProps.incident.id &&
    prevProps.incident.title === nextProps.incident.title &&
    prevProps.incident.description === nextProps.incident.description &&
    prevProps.incident.severity === nextProps.incident.severity &&
    prevProps.incident.timeLimit === nextProps.incident.timeLimit &&
    prevProps.incident.category === nextProps.incident.category &&
    prevProps.incident.enabled === nextProps.incident.enabled &&
    prevProps.incident.order === nextProps.incident.order &&
    prevProps.index === nextProps.index &&
    prevProps.readOnly === nextProps.readOnly &&
    prevProps.totalIncidents === nextProps.totalIncidents &&
    prevProps.onEdit === nextProps.onEdit &&
    prevProps.onDelete === nextProps.onDelete &&
    prevProps.onToggle === nextProps.onToggle &&
    prevProps.onReorder === nextProps.onReorder
  );
});

IncidentGridCard.displayName = 'IncidentGridCard';